import { useEffect, useState, useRef, useCallback } from 'react'
import { getWsBaseUrl } from '../config'
import type { Device } from '../types'
import { getToken } from './api'

interface UseWebSocketOptions {
  enabled?: boolean
  onMessage?: (data: unknown) => void
  onError?: (error: Event) => void
}

export function useWebSocket({ enabled = true, onMessage, onError }: UseWebSocketOptions) {
  const [connected, setConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)

  const connect = useCallback(() => {
    if (!enabled) return

    try {
      const token = getToken()
      const url = new URL('ws/devices', getWsBaseUrl().replace(/\/$/, '') + '/')
      if (token) {
        url.searchParams.set('token', token)
      }

      const ws = new WebSocket(url.toString())
      wsRef.current = ws

      ws.onopen = () => {
        setConnected(true)
        reconnectAttemptsRef.current = 0
      }

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data)
          onMessage?.(parsed)
        } catch {
          onMessage?.(event.data)
        }
      }

      ws.onerror = (error) => {
        setConnected(false)
        onError?.(error)
      }

      ws.onclose = () => {
        setConnected(false)
        wsRef.current = null
        if (!enabled) return
        const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30000)
        reconnectAttemptsRef.current += 1
        reconnectTimeoutRef.current = setTimeout(connect, delay)
      }
    } catch (err) {
      console.error('WebSocket connection error:', err)
      onError?.(err as Event)
    }
  }, [enabled, onMessage, onError])

  useEffect(() => {
    connect()

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close()
      }
    }
  }, [connect])

  return { connected }
}

export function useRealtimeDevices(initialDevices: Device[] = []) {
  const [devices, setDevices] = useState<Device[]>(initialDevices)
  const [wsEnabled, setWsEnabled] = useState(true)

  useEffect(() => {
    setDevices(initialDevices)
  }, [initialDevices])

  const handleMessage = useCallback((data: unknown) => {
    if (Array.isArray(data)) {
      setDevices(data as Device[])
    } else if (data && typeof data === 'object') {
      const payload = data as { type?: string; device?: Device; devices?: Device[] }
      if (payload.type === 'devices' && Array.isArray(payload.devices)) {
        setDevices(payload.devices)
      } else if (payload.type === 'device' && payload.device) {
        setDevices(prev => {
          const idx = prev.findIndex(d => d.id === payload.device!.id)
          if (idx === -1) return [...prev, payload.device!]
          const next = [...prev]
          next[idx] = payload.device!
          return next
        })
      }
    }
  }, [])

  const { connected } = useWebSocket({ enabled: wsEnabled, onMessage: handleMessage })

  return { devices, setDevices, wsEnabled, setWsEnabled, wsConnected: connected }
}
